import gql from 'graphql-tag';

import POSMetaDataFragment from 'shared/graphql/product/pos-meta-data-fragment.gql';
import fragments from './fragments';

export const activeProductsCheck = gql`
  ${POSMetaDataFragment}

  query ActiveProductsCheck($productsFilter: activeProductsCheckInput!, $includeEnterpriseSpecials: Boolean = false) {
    activeProductsCheck(filter: $productsFilter, includeEnterpriseSpecials: $includeEnterpriseSpecials) {
      _id
      enterpriseProductId
      medicalSpecialPrices
      medicalPrices
      recSpecialPrices
      wholesalePrices
      recPrices
      Options
      rawOptions
      limitsPerCustomer {
        key
        value
      }
      manualInventory {
        option
        inventory
      }
      Name
      special
      specialData {
        bogoSpecials {
          applyRewardsToConditions
          excludeConditionItemsFromRewards
          bogoConditionLogicOperator
          bogoConditions {
            _id
            brandId
            brandIds
            brandName
            brandNames
            categoryIds
            categoryName
            categoryNames
            enterpriseProductIds
            exclusions {
              _id
              brandIds
              brandNames
              categoryIds
              categoryNames
              enterpriseProductIds
              inventoryTags
              matchOperator
              productGroup
              productIds
              productTags
              strainIds
              subcategoryIds
              subcategoryNames
              vendorIds
              weights
              weightOperator
            }
            inventoryTags
            productGroup
            productId
            productIds
            productTags
            quantity
            strainIds
            subcategoryIds
            subcategoryNames
            vendorIds
            weight
            weightOperator
          }
          bogoRewardLogicOperator
          bogoRewards {
            _id
            brandId
            brandIds
            brandName
            brandNames
            categoryIds
            categoryName
            categoryNames
            dollarDiscount
            enterpriseProductIds
            exclusions {
              _id
              brandIds
              brandNames
              categoryIds
              categoryNames
              enterpriseProductIds
              inventoryTags
              matchOperator
              productGroup
              productIds
              productTags
              strainIds
              subcategoryIds
              subcategoryNames
              vendorIds
              weights
              weightOperator
            }
            inventoryTags
            productGroup
            productId
            productIds
            percentDiscount
            productTags
            quantity
            strainIds
            subcategoryIds
            subcategoryNames
            targetPrice
            vendorIds
            weight
          }
          createdBy
          directPOSProductIdMapping
          rawPOSWeightMapping
          discountBehavior
          discountToCart {
            _id
            enabled
            discountType
            value
          }
          endStamp
          excludedProducts {
            conditions {
              _id
              key
              Name
            }
            rewards {
              _id
              key
              Name
            }
          }
          isRecurring
          itemsForAPrice {
            _id
            enabled
            value
          }
          discountBundle {
            _id
            applyTo
            discountType
            enabled
            limit
            value
          }
          menuType
          minorVersion
          paymentRestrictions {
            payByBankSignupIncentive
          }
          recurringEndDate
          redemptionLimit
          specialId
          specialName
          specialType
          totalQuantity {
            enabled
            maxQuantity
            quantity
            quantityOperator
          }
          totalWeight {
            enabled
            weight
            weightOperator
          }
          totalSpend {
            enabled
            maximumSpend
            minimumSpend
            spendOperator
          }
          version
        }
        saleSpecials {
          createdBy
          directPOSProductIdMapping
          rawPOSWeightMapping
          discount
          dollarDiscount
          eligibleProductOptions
          menuType
          minorVersion
          paymentRestrictions {
            payByBankSignupIncentive
          }
          percentDiscount
          saleDiscounts {
            _id
            brandIds
            brandNames
            categoryIds
            categoryNames
            discountAmount
            discountType
            enterpriseProductIds
            exclusions {
              _id
              brandIds
              brandNames
              categoryIds
              categoryNames
              enterpriseProductIds
              inventoryTags
              matchOperator
              productGroup
              productIds
              productTags
              strainIds
              subcategoryIds
              subcategoryNames
              vendorIds
              weights
              weightOperator
            }
            inventoryTags
            productGroup
            productIds
            productTags
            strainIds
            subcategoryIds
            subcategoryNames
            vendorIds
            weights
            weightOperator
          }
          specialName
          source
          sourceId
          specialId
          specialRestrictions
          specialType
          stackingBehavior
          nonStackingBehavior
          stackingMode
          targetPrice
          version
        }
      }
      POSMetaData {
        ...POSMetaDataFragment
      }
      bottleDepositTaxCents
    }
  }
`;

export const adminFilteredProducts = gql`
  ${fragments.adminBaseProductFragment}

  query AdminFilteredProducts($productsFilter: productsFilterInput!, $page: Int, $perPage: Int) {
    filteredProducts(filter: $productsFilter, page: $page, perPage: $perPage) {
      products {
        ...adminBaseProductFragment
        libraryProduct {
          id
          name
        }
        productTemplate
        connectedCount
        forcedPotencyUnit
      }
      queryInfo {
        totalCount
      }
    }
  }
`;

export const adminIndividualFilteredProduct = gql`
  ${fragments.adminBaseProductFragment}

  query AdminIndividualFilteredProduct($productsFilter: productsFilterInput!) {
    filteredProducts(filter: $productsFilter) {
      products {
        ...adminBaseProductFragment
        stockImage
        stockImageAliases
        stockImageCategory
        productTemplate
        libraryProduct {
          id
          name
        }
        terpenes {
          id
          terpeneId
          value
          unit
          unitSymbol
          libraryTerpene {
            name
          }
        }
        cannabinoidsV2 {
          value
          unit
          cannabinoidId
          cannabinoid {
            name
          }
        }
        connectedCount
        forcedPotencyUnit

        connectedByUser {
          firstName
          lastName
          fullName
        }
        updatedByUser {
          firstName
          lastName
          fullName
        }
        updatedByAdminAt
      }
      queryInfo {
        totalCount
      }
    }
  }
`;

export const getProductChangeEvents = gql`
  query GetProductChangeEvents($productId: ID!) {
    productChangeEvents(productId: $productId) {
      id
      createdAt
      createdBy
      createdByUser {
        firstName
        lastName
        fullName
      }
      event
      metadata
      newValues
      oldValues
      productId
      versionId
    }
  }
`;

export const matchingProducts = gql`
  query MatchingProducts(
    $dispensaryIds: [String]
    $limit: Int
    $name: String
    $productLibraryState: String
    $bypassOnlineThresholds: Boolean
  ) {
    matchingProducts(
      dispensaryIds: $dispensaryIds
      limit: $limit
      name: $name
      productLibraryState: $productLibraryState
      bypassOnlineThresholds: $bypassOnlineThresholds
    ) {
      id
      _id
      Name
      brand {
        id
        name
      }
      brandName
      type
      strainType
      THC
      THCContent {
        unit
        range
      }
      CBD
      CBDContent {
        unit
        range
      }
      Image
      recPrices
    }
  }
`;

export const bulkLibraryProductUpdateStatus = gql`
  query BulkLibraryProductUpdateStatus($jobId: String!) {
    bulkLibraryProductUpdateStatus(jobId: $jobId) {
      meta {
        status
      }
    }
  }
`;
